import { AxiosError } from 'axios';
import React from 'react';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import {
  IconButton,
  ListItemIcon,
  makeStyles,
  Menu,
  MenuItem
} from '@material-ui/core';
import { Publish as IconPublish } from '@material-ui/icons';

import {
  EditIcon,
  TableActionIcon
} from '@checkit/react-components/build/components/Icons';

import MaterialUiLink from '../../components/MaterialUiLink';
import { useAxiosDispatch } from '../../middleware/axios';
import { createSnackbar } from '../../notifications/actions';
import {
  isPublishLibrarySuccess,
  publishLibrary
} from '../actions';
import { Library } from '../state';

import { messages } from './LibrariesList.translations';

/** Gets the relative url to the library's edit page. */
const getLibraryUrl = (library: Library) => `/libraries/${library.code}`;

const useStyles = makeStyles(( ) => ({
  actionIcon: {
    // see CSD-11917, whitespace around the icons is awkward
    minWidth: 35
  }
}));

/**
 * Extracts the response error message from an AxiosError, if the given error
 * does actually have an associated response with an error message.
 * Otherwise, returns `null`.
 */
function extractErrorMessage(error: AxiosError): string | null {
  const response = error.response?.data;
  if (response == null) { return null; }
  if (typeof response !== 'object') { return null; }
  // type assertion because https://github.com/microsoft/TypeScript/issues/38801
  return (response as { message?: string }).message ?? null;
}

export function LibraryActions({ library }: {
  /** The library for which actions are being displayed. */
  library: Library;
}) {
  const { formatMessage } = useIntl( );
  const dispatch = useAxiosDispatch( );
  const classes = useStyles( );

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

  const isMenuOpen = anchorEl != null;

  const handleOpenMenu = (event: React.MouseEvent<HTMLElement>) => void setAnchorEl(event.currentTarget);
  const handleCloseMenu = ( ) => void setAnchorEl(null);

  function handlePublish( ) {
    handleCloseMenu( );

    const action = publishLibrary(library.code);
    dispatch(action).then(function handlePublishResult(result) {
      if (isPublishLibrarySuccess(result)) {
        dispatch(createSnackbar({
          message: formatMessage(messages['libraries.list.publish.success']),
          options: { variant: 'success' }
        }));
      } else {
        const error = extractErrorMessage(result.error);
        const isChildlessPublish = error === messages['libraries.list.publish.failure.noChildren'].id;
        if (isChildlessPublish) {
          dispatch(createSnackbar({
            message: formatMessage(messages['libraries.list.publish.failure.noChildren']),
            options: { variant: 'error' }
          }));
        } else {
          // to be handled by the catch block
          throw new Error(`Unexpected resulting action: "${action.type}"`);
        }
      }
    }).catch(function handleError(/* err: unknown */) {
      // TODO: log the error somewhere?
      dispatch(createSnackbar({
        message: formatMessage(messages['libraries.list.publish.failure']),
        options: { variant: 'error' }
      }));
    });
  }

  return (
    <>
      <IconButton
        aria-label={formatMessage(messages['libraries.list.actions'])}
        aria-controls="long-menu"
        aria-haspopup="true"
        onClick={handleOpenMenu}
      >
        <TableActionIcon />
      </IconButton>

      <Menu
        anchorEl={anchorEl}
        open={isMenuOpen}
        onClose={handleCloseMenu}
      >
        <MenuItem
          onClick={handleCloseMenu}
          component={MaterialUiLink}
          to={getLibraryUrl(library)}
        >
          <ListItemIcon className={classes.actionIcon}>
            <EditIcon />
          </ListItemIcon>
          {formatMessage(messages['libraries.list.update'])}
        </MenuItem>

        <MenuItem onClick={handlePublish}>
          <ListItemIcon className={classes.actionIcon}>
            <IconPublish />
          </ListItemIcon>
          {formatMessage(messages['libraries.list.publish'])}
        </MenuItem>
      </Menu>
    </>
  );
}
